import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getTranslate } from 'react-localize-redux';
import { Link, Prompt } from 'react-router-dom';

import {
    IconButton, Button, Typography, FormControlLabel, Checkbox,
} from '@material-ui/core';
import * as UserStore from '../../../app/store/UserStore';
import {
    validateAllFields, validateField,
} from '../../../youwell-common/validationUtils';

import { getValidators } from './validation';
import { VimoInput, Selector } from '../../../common/components';
import { appRoles, loginLevels } from '../../../youwell-common/constants';
import { SaveIcon, CancelIcon } from '../../../common/icons';
import { hasValue } from '../../../youwell-common/objectUtils';
import urls from '../../../app/urls';
import Auth from '../../../youwell-common/Auth';
import UserMenu from './UserMenu';
import { vimoCreateRoles } from '../../../common/constants';

const styles = theme => ({
    root: {
        display: 'flex',
        padding: theme.spacing(2),
        background: theme.palette.grey[50],
    },
    textFields: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
    },
    field: {
        margin: theme.spacing(1),
        flexGrow: 1,
    },
    actionContainer: {
        margin: theme.spacing(1),
        marginLeft: -theme.spacing(1),
        border: '2px solid transparent',
        background: theme.palette.common.white,
    },
    infoMessage: {
        flex: '1 1 auto',
        margin: theme.spacing(1),
        padding: theme.spacing(2),
        background: theme.palette.common.white,
        border: theme.borders.green,
        color: theme.colors.blue4,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        margin: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    },
    button: {
        marginLeft: theme.spacing(1),
    },
    buttonIcon: {
        marginRight: theme.spacing(1),
    },
    role: {
        display: 'flex',
    },
});


const defaultState = {
    name: '',
    fnr: '',
    email: '',
    phone: '',
    extendedProperties: null,
    sendWelcomeEmail: true,
};

const stateFromData = (data, clientConfig) => {
    const initialState = {
        ...defaultState,
        hasUnsavedChanges: false,
        rolesUpdated: false,
        mappedRoles: [],
        roleForNewUser: appRoles.therapist,
        editing: !data.id,
        validators: getValidators(clientConfig, !data?.id),
        validationErrors: {},
    };

    if (!data) {
        return initialState;
    }

    return Object.keys(defaultState).reduce(
        (obj, key) => (hasValue(data[key]) ? { ...obj, [key]: data[key] } : { ...obj }),
        {
            ...initialState,
            mappedRoles: data.roles?.map(d => ({ id: d })),
        },
    );
};

class UserDetails extends React.Component {
    state = stateFromData(this.props.userData, this.props.clientConfig);

    handleChange = name => (event) => {
        this.setState({
            [name]: event.target.value,
            hasUnsavedChanges: true,
        }, () => {
            if (this.state.validators[name]) {
                const validationErrors = validateField(this.state, this.state.validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    }

    handleRolesChange = (event) => {
        this.setState({ mappedRoles: event.target.value, rolesUpdated: true }, () => {
            const validationErrors = validateField(this.state, this.state.validators.mappedRoles, 'mappedRoles'); // eslint-disable-line react/no-access-state-in-setstate
            this.setState({ validationErrors });
        });
    }

    toggleCheckedChange = name => (event) => {
        this.setState({
            [name]: event.target.checked,
            hasUnsavedChanges: true,
        });
    }

    handleExtendePropsChange = name => (event) => {
        const value = event.target.value;

        this.setState(prevState => ({
            extendedProperties: {
                ...prevState.extendedProperties,
                [name]: value,
            },
            hasUnsavedChanges: true,
        }));
    }

    startEdit = () => {
        this.setState({ editing: true });
    }

    cancel = () => {
        this.setState(stateFromData(this.props.userData, this.props.clientConfig));
    }

    saveChanges = () => {
        // eslint-disable-next-line react/no-access-state-in-setstate
        const validationErrors = validateAllFields(this.state, this.state.validators);

        if (!validationErrors.hasErrors) {
            const saveData = Object.keys(defaultState).reduce(
                (obj, key) => (this.state[key] ? { ...obj, [key]: this.state[key] } : { ...obj }),
                {
                    id: this.props.userData.id,
                },
            );

            const callback = () => {
                this.setState({ editing: false, hasUnsavedChanges: false, rolesUpdated: false });
            };

            if (this.props.userData.id) {
                if (this.state.rolesUpdated) {
                    saveData.updatedRoles = this.state.mappedRoles.map(d => d.id);
                }
                this.props.updateElement(saveData, { callback });
            } else {
                saveData.role = this.state.roleForNewUser;
                this.props.insertElement(saveData, { callback, redirectWithElementId: urls.inside.users.home });
            }
        }
        this.setState({ validationErrors });
    }

    render() {
        const {
            classes, userData, translate, profileData,
        } = this.props;

        if (!userData || !profileData) {
            return null;
        }

        const roles = vimoCreateRoles
            .map(item => ({ id: item, name: translate(`user.appRoles_${item}`) }));

        const loginLevel = Auth.getLoginLevel();
        const isLoggedInLevel4 = loginLevel === loginLevels.level4;
        const readOnly = !this.state.editing;

        return (
            <div className={classes.root}>
                <Prompt
                    when={this.state.hasUnsavedChanges}
                    message={translate('navigate_away_unsaved_changes')}
                />
                <div className={classes.textFields}>
                    <div className={classes.row}>
                        <VimoInput
                            id="name"
                            label="user.name"
                            value={this.state.name}
                            readOnly={readOnly}
                            onChange={this.handleChange('name')}
                            className={classes.field}
                            validationError={this.state.validationErrors.name}
                        />
                        {userData.id && !this.state.editing && (
                            <div className={classes.actionContainer}>
                                <UserMenu userData={userData} startEditDetails={this.startEdit} />
                            </div>
                        )}
                    </div>

                    {isLoggedInLevel4 && (
                        <div className={classes.row}>
                            <VimoInput
                                id="fnr"
                                label="user.fnr"
                                value={this.state.fnr}
                                readOnly={readOnly}
                                onChange={this.handleChange('fnr')}
                                className={classes.field}
                                validationError={this.state.validationErrors.fnr}
                            />
                        </div>
                    )}

                    <div className={classes.row}>
                        <VimoInput
                            id="email"
                            label="user.email"
                            value={this.state.email}
                            readOnly={readOnly}
                            onChange={this.handleChange('email')}
                            className={classes.field}
                            validationError={this.state.validationErrors.email}
                        />

                        <VimoInput
                            id="phone"
                            label="user.phone"
                            value={this.state.phone}
                            readOnly={readOnly}
                            onChange={this.handleChange('phone')}
                            className={classes.field}
                            validationError={this.state.validationErrors.phone}
                        />
                    </div>

                    <div className={classes.row}>
                        {userData.id ? (
                            <Selector
                                id="role"
                                multiple
                                items={roles}
                                label={translate('user.role')}
                                values={this.state.mappedRoles}
                                disabled={readOnly}
                                onChange={this.handleRolesChange}
                                className={classes.field}
                                validationError={this.state.validationErrors.mappedRoles}
                            />
                        ) : (
                            <Selector
                                id="role"
                                items={roles}
                                label={translate('user.role')}
                                value={this.state.roleForNewUser}
                                disabled={readOnly}
                                onChange={this.handleChange('roleForNewUser')}
                                className={classes.field}
                                validationError={this.state.validationErrors.roleForNewUser}
                            />
                        )}
                    </div>

                    {!userData.id && (
                        <div className={classes.row}>
                            <FormControlLabel
                                className={classes.field}
                                control={(
                                    <Checkbox
                                        checked={!!this.state.sendWelcomeEmail}
                                        onChange={this.toggleCheckedChange('sendWelcomeEmail')}
                                        value="sendWelcomeEmail"
                                        color="primary"
                                    />
                                )}
                                label={translate('user.sendWelcomeEmail')}
                            />
                        </div>
                    )}

                    {this.state.extendedProperties && (
                        <div className={classes.extra}>
                            {Object.keys(this.state.extendedProperties).map(key => (
                                <div className={classes.row} key={key}>
                                    <VimoInput
                                        id={`extra_${key}`}
                                        label={`user.extendedProps.${key}`}
                                        value={this.state.extendedProperties[key]}
                                        readOnly={readOnly}
                                        onChange={this.handleExtendePropsChange(key)}
                                        className={classes.field}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {userData.generatedPassword && (
                        <div className={classes.row}>
                            <Typography color="inherit" className={classes.infoMessage}>
                                {`${translate('user.temporaryPassword', { password: userData.generatedPassword })}`}
                            </Typography>
                        </div>
                    )}
                    {userData.addedExistingUser && (
                        <div className={classes.row}>
                            <Typography color="inherit" className={classes.infoMessage}>
                                {`${translate('user.addedExistingUser')}`}
                            </Typography>
                        </div>
                    )}

                    <div className={classes.actions}>
                        {/* {!this.state.editing && (
                            <IconButton onClick={this.startEdit} className={classes.iconButton}>
                                <EditIcon />
                            </IconButton>
                        )} */}
                        {this.state.editing && (
                            userData.id ? (
                                <IconButton onClick={this.cancel} className={classes.iconButton} color="secondary">
                                    <CancelIcon />
                                </IconButton>
                            ) : (
                                <Button component={Link} to={urls.inside.users.home} className={classes.button}>
                                    <CancelIcon className={classes.buttonIcon} />
                                    {translate('common.cancel')}
                                </Button>
                            )
                        )}
                        {this.state.editing && (
                            userData.id ? (
                                <IconButton onClick={this.saveChanges} className={classes.iconButton} color="primary">
                                    <SaveIcon />
                                </IconButton>
                            ) : (
                                <Button onClick={this.saveChanges} className={classes.button} variant="contained" color="primary">
                                    <SaveIcon className={classes.buttonIcon} />
                                    {translate('common.save')}
                                </Button>
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    }
}


UserDetails.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    clientConfig: PropTypes.object.isRequired,
    profileData: PropTypes.object.isRequired,
    insertElement: PropTypes.func.isRequired,
    updateElement: PropTypes.func.isRequired,
    userData: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    profileData: state.auth.profileData,
    clientConfig: state.auth.clientConfig,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(UserStore.actionCreators, dispatch),
});


export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(UserDetails));
